import { type TFunction } from 'i18next';

export enum FileCheckResult {
  OK,
  INVALID_FILE_TYPE,
  ONLY_ONE_FILE_ALLOWED,
  MALFORMED_FILE,
  FILES_TOO_LARGE,
}

export function mapResultToErrorText(
  result: FileCheckResult,
  t: TFunction<'translation', undefined>,
  maximumFileSizeBytes?: number,
): string | undefined {
  switch (result) {
    case FileCheckResult.OK:
      return undefined;
    case FileCheckResult.MALFORMED_FILE:
      return t('home.input.malformed_file');
    case FileCheckResult.INVALID_FILE_TYPE:
      return t('home.input.invalid_file_type');
    case FileCheckResult.ONLY_ONE_FILE_ALLOWED:
      return t('home.input.only_one_file_allowed');
    case FileCheckResult.FILES_TOO_LARGE:
      return t('home.input.file_too_large', {
        maximumFileSizeMB: Math.ceil(maximumFileSizeBytes / (1024 * 1024)),
      });
    default:
      console.warn('call to [mapResultToErrorText] executed default case.');
      return undefined;
  }
}

export function shallowCheckFiles(
  allowMultipleFiles: boolean,
  allowedFileTypes: string[],
  files?: FileList,
): FileCheckResult {
  if (!files) {
    return FileCheckResult.INVALID_FILE_TYPE;
  }

  if (!allowMultipleFiles && files.length > 1) {
    return FileCheckResult.ONLY_ONE_FILE_ALLOWED;
  }

  if (files.length === 0) {
    return FileCheckResult.INVALID_FILE_TYPE;
  }

  if (allowedFileTypes) {
    for (let i = 0; i < files.length; i++) {
      // shallow check for file type
      if (!allowedFileTypes.includes(files[i].type)) {
        return FileCheckResult.INVALID_FILE_TYPE;
      }
    }
  }

  return FileCheckResult.OK;
}

export async function deepCheckFiles(
  allowedFileTypes: string[],
  files?: FileList,
): Promise<FileCheckResult> {
  if (allowedFileTypes && files) {
    const checks = Array.from(files).map(async (file) => {
      const buffer = await file.arrayBuffer();
      const { fileTypeFromBuffer } = await import('file-type');
      const typeObj = await fileTypeFromBuffer(new Uint8Array(buffer));
      return allowedFileTypes.includes(typeObj?.mime);
    });

    const results = await Promise.all(checks);
    if (results.some((isValid) => !isValid)) {
      return FileCheckResult.MALFORMED_FILE;
    }
  }

  return FileCheckResult.OK;
}

export async function completeCheck(
  allowMultipleFiles: boolean,
  allowedFileTypes: string[],
  maximumFileSizeBytes: number,
  files?: FileList,
): Promise<FileCheckResult> {
  let result = shallowCheckFiles(allowMultipleFiles, allowedFileTypes, files);
  if (result !== FileCheckResult.OK) {
    return result;
  }

  result = await deepCheckFiles(allowedFileTypes, files);
  if (result !== FileCheckResult.OK) {
    return result;
  }

  let totalFileSize = 0;
  for (let i = 0; i < files.length; i++) {
    totalFileSize += files.item(i).size;
  }
  if (totalFileSize > maximumFileSizeBytes) {
    return FileCheckResult.FILES_TOO_LARGE;
  }

  return result;
}
